import { NavLink } from "react-router-dom";
import { useInbox } from "@/components/inbox/InboxProvider";
import { isLate } from "@/components/inbox/inboxUtils";
import { useAuth } from "@/components/auth/AuthProvider";
import { useChat, isChatLate } from "@/components/chat/ChatProvider";
import { useServices } from "@/lib/services";
import { BlinkDot } from "@/components/avisos/BlinkDot";
import {
  LayoutDashboard, Users, Receipt, Zap, DownloadCloud, History, Settings as Cog, Server, CircleDot, MessageCircle, LogOut, Database, MessagesSquare, Bell, Info,
} from "lucide-react";

const items = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-clients" },
  { to: "/faturas", label: "Faturas & Cobrança", icon: Receipt, testid: "nav-invoices" },
  { to: "/automacoes", label: "Automações", icon: Zap, testid: "nav-automations" },
  { to: "/importador", label: "Importador / Extensão", icon: DownloadCloud, testid: "nav-importer", admin: true },
  { to: "/dados", label: "Dados", icon: Database, testid: "nav-dados", admin: true },
  { to: "/respostas", label: "Respostas", icon: MessageCircle, testid: "nav-responses", badge: true },
  { to: "/chat", label: "Chat do site", icon: MessagesSquare, testid: "nav-chat", chatBadge: true },
  { to: "/historico", label: "Histórico de Envios", icon: History, testid: "nav-logs" },
  { to: "/avisos", label: "Avisos", icon: Bell, testid: "nav-avisos", dueBadge: true },
  { to: "/avisos/informacoes", label: "Informações", icon: Info, testid: "nav-informacoes", sub: true },
  { to: "/configuracoes", label: "Configurações", icon: Cog, testid: "nav-settings", admin: true },
];

export default function Sidebar() {
  const { open, items: inboxItems } = useInbox();
  const late = inboxItems.some(isLate);
  const { user, logout } = useAuth();
  const chat = useChat();
  const chatWaiting = chat.items.filter((s) => s.waiting_since || s.unread > 0);
  const chatLate = chat.items.some(isChatLate);
  const { alerts } = useServices();
  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-[#0F141F] border-r border-[#1F293D] z-40 hidden lg:flex flex-col">
      <div className="px-5 py-5 border-b border-[#1F293D]">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-500/20">
            <Server className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-[15px] font-extrabold tracking-tight text-white leading-none">SYSLOG</div>
            <div className="text-[11px] text-slate-400 mt-0.5">CRM de Cobrança</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items.filter((it) => !it.admin || user?.role === "admin").map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end || it.dueBadge}
            data-testid={it.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 ${it.sub ? "ml-6 py-2 text-[13px]" : "py-2.5"} px-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? "bg-sky-500/15 text-sky-300 border border-sky-500/25"
                  : "text-slate-400 hover:text-white hover:bg-[#1A2234] border border-transparent"
              }`
            }
          >
            <it.icon className="h-[18px] w-[18px]" />
            {it.label}
            {it.badge && open > 0 && <span data-testid="nav-responses-badge" data-late={late} className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-bold ${late ? "animate-pulse bg-red-500 text-white" : "bg-emerald-500 text-slate-950"}`}>{open}</span>}
            {it.dueBadge && alerts > 0 && <span data-testid="nav-avisos-badge" className="ml-auto flex items-center gap-1.5 text-[11px] font-bold text-orange-300"><BlinkDot testid="nav-avisos-dot" />{alerts}</span>}
            {it.chatBadge && chatWaiting.length > 0 && <span data-testid="nav-chat-badge" data-late={chatLate} className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-bold ${chatLate ? "animate-pulse bg-red-500 text-white" : "bg-sky-400 text-slate-950"}`}>{chatWaiting.length}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="mx-3 mb-2 flex items-center gap-2 rounded-lg border border-[#1F293D] bg-[#111827] px-3 py-2">
        <div className="min-w-0 flex-1">
          <div data-testid="sidebar-user" className="truncate text-xs font-semibold text-slate-200">{user?.username}</div>
          <div data-testid="sidebar-role" className="text-[11px] text-slate-500">{user?.role === "admin" ? "Administrador" : "Atendente"}</div>
        </div>
        <button data-testid="logout-button" onClick={logout} title="Sair" className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-[#1A2234] hover:text-red-300"><LogOut className="h-4 w-4" /></button>
      </div>
      <div className="px-4 py-4 border-t border-[#1F293D]">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <CircleDot className="h-3.5 w-3.5 text-emerald-400" />
          <span>Servidor <span className="text-slate-200 font-medium">WEPLAY</span></span>
        </div>
        <div className="text-[11px] text-slate-500 mt-1">Painel SYSLOG · via API</div>
      </div>
    </aside>
  );
}
